import { LineItem } from "./order";

/**
 * FUPOF-191 — the one place the quote-level notes string is built and taken
 * apart again, mirroring `line_notes.ts` for a line's production notes.
 *
 * The store keeps `Order.notes` as the rep's own text and nothing else. The
 * multiplier review warning is *derived* from the cart's multipliers at compose
 * time and stripped back off on the way in — the same contract the spigot and
 * beauty-plate sentences follow on `item_details.notes` (`CustomizeBase`
 * composes them in `addNotesToQuote`, strips them in `setNotes`).
 *
 * Deriving rather than persisting is what keeps the rep's textarea holding only
 * their own words, and it is what stops the warning from compounding: quote
 * notes round-trip through NetSuite, and header drift is auto-applied NS-wins,
 * so a stored warning would come back and be composed a second time.
 *
 * `composeQuoteNotes` and `stripQuoteNotes` must stay exact inverses.
 */

const MULTIPLIER_REVIEW_HEAD = "MULTIPLIER REVIEW: ";

/**
 * The delimiters an earlier build wrote into `Order.notes` directly. Existing
 * drafts and NetSuite quotes still carry them, so the strip step has to clear
 * them out even though nothing composes them any more.
 */
const LEGACY_BLOCK = /\s*\[Multiplier review\][\s\S]*?\[\/Multiplier review\]/g;

/** Float multipliers need rounding before they're compared or grouped. */
const multiplierKey = (item: LineItem): number =>
  Number((item.multiplier ?? 1).toFixed(4));

/**
 * The cart's multipliers, each mapped to the 1-based positions using it.
 * Exported so the UI can render the same grouping it warns about.
 */
export const multiplierGroups = (items: LineItem[]): Map<number, number[]> => {
  const groups = new Map<number, number[]>();
  items.forEach((item, i) => {
    const key = multiplierKey(item);
    const positions = groups.get(key);
    if (positions) positions.push(i + 1);
    else groups.set(key, [i + 1]);
  });
  return groups;
};

/**
 * The warning sentence, or "" when the cart is uniform. The multiplier is a
 * quote-wide figure — EditMultiplier tells the user it "is applied for all
 * quote line items" — so a cart holding more than one is a quote no single
 * multiplier describes. Which one is correct can't be decided here, so it goes
 * to the rep rather than submitting unremarked.
 */
export const multiplierWarning = (items: LineItem[]): string => {
  const groups = multiplierGroups(items);
  if (groups.size < 2) return "";
  const parts = Array.from(groups.entries()).map(([multiplier, positions]) => {
    const label = positions.length > 1 ? "items" : "item";
    return `x ${multiplier} (${label} ${positions.join(", ")})`;
  });
  return (
    `This quote uses more than one line multiplier: ${parts.join(", ")}. ` +
    "Please review before submitting."
  );
};

/**
 * The inverse of `composeQuoteNotes` — recovers the rep's own text. Safe to
 * call on a string that was never composed, which is what makes it the right
 * guard on every path that writes `Order.notes`.
 */
export const stripQuoteNotes = (
  composed: string | null | undefined
): string => {
  if (!composed) return "";
  // The heading runs to the end of its paragraph: the warning is authored as a
  // single line, so a blank line (or the string's end) closes it.
  const headed = new RegExp(
    `${MULTIPLIER_REVIEW_HEAD}[^\\n]*(?:\\n(?!\\n)[^\\n]*)*(?:\\n\\n|$)`,
    "g"
  );
  return composed
    .replace(LEGACY_BLOCK, "")
    .replace(headed, "")
    .trim();
};

/**
 * Flattens the rep's notes and any derived warning into the single string
 * NetSuite stores on the quote. The warning leads so it isn't buried under a
 * long set of delivery instructions.
 */
export const composeQuoteNotes = (
  notes: string | null | undefined,
  items: LineItem[]
): string => {
  const freeText = stripQuoteNotes(notes);
  const warning = multiplierWarning(items);
  if (!warning) return freeText;
  const headed = `${MULTIPLIER_REVIEW_HEAD}${warning}`;
  return freeText ? `${headed}\n\n${freeText}` : headed;
};
